import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  XCircle,
  MinusCircle,
  CircleDashed,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Award,
  HelpCircle,
} from "lucide-react";
import { showToast as toast } from "../../../../components/ClassicToast";
import { stemProjectApi } from "../../services/stemProjectApi";
import { stemProgressApi } from "../../services/stemProgressApi";

export default function StageQuestionsView({
  stage,
  project,
  isCompleted,
  onCompleteStage,
  onNextStage,
  isLastStage,
}) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active question index for one-by-one modes
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // userAnswers: { [questionId]: selectedOptionIndexOrValue }
  const [userAnswers, setUserAnswers] = useState({});

  // Solved state specifically for ONE_BY_ONE_TRUE mode: { [questionId]: true }
  const [solvedMap, setSolvedMap] = useState({});

  // Visual tracking for wrong click in ONE_BY_ONE_TRUE mode
  const [wrongClickOptId, setWrongClickOptId] = useState(null);

  // Evaluation submission state: always false initially (show questions first, evaluation after submit)
  const [submitted, setSubmitted] = useState(false);

  // True once this student's result for the stage is saved: the stage cannot be taken again
  const [locked, setLocked] = useState(false);

  // Show a saved result as the answer sheet (read-only)
  const showSavedResult = (evaluation) => {
    setUserAnswers(evaluation?.answers || {});
    setSubmitted(true);
    setLocked(true);
  };

  // Determine questionDisplayMode: 'ALL', 'ONE_BY_ONE_TRUE', 'ONE_BY_ONE_TRUE_FALSE'
  const displayMode = (stage?.questionDisplayMode || "ALL").toUpperCase();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setSolvedMap({});
    setWrongClickOptId(null);
    setSubmitted(false);
    setLocked(false);

    // If this student already submitted the stage, open straight on their answer sheet
    stemProgressApi
      .getStageEvaluation(stage.id)
      .then((evaluation) => {
        if (!cancelled && evaluation) showSavedResult(evaluation);
      })
      .catch((err) => {
        console.error("[StageQuestionsView] Failed to load saved result:", err);
      });

    console.log("[StageQuestionsView] Fetching questions for stage:", {
      stageId: stage.id,
      stageName: stage.name,
      displayMode,
    });

    stemProjectApi
      .getStageQuestions(stage.id)
      .then((data) => {
        if (!cancelled) {
          console.log("[StageQuestionsView] Received questions data:", data);
          setQuestions(data || []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[StageQuestionsView] Error loading questions:", err);
          setError(err.message || "Failed to load questions");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [stage.id, stage.name, displayMode]);

  // Helper to get the correct option for a question
  const getCorrectOption = (q) => {
    if (!q || !q.options) return null;
    return q.options.find((opt) => opt.isCorrect === true) || null;
  };

  // Check if user answer is correct for a question
  const isQuestionAnsweredCorrectly = (q) => {
    const answer = userAnswers[q.id];
    if (answer === undefined || answer === null) return false;

    const correctOpt = getCorrectOption(q);
    if (!correctOpt) return false;
    const selectedOpt =
      typeof answer === "number"
        ? q.options?.[answer]
        : q.options?.find((o) => o.id === answer || o.id === Number(answer));
    return selectedOpt ? Boolean(selectedOpt.isCorrect) : false;
  };

  // Handle option selection
  const handleSelectOption = (q, optIndex) => {
    if (submitted) return;

    const opt = q.options?.[optIndex];
    console.log("[StageQuestionsView] Selected option:", {
      questionId: q.id,
      optIndex,
      opt,
      displayMode,
    });

    if (displayMode === "ONE_BY_ONE_TRUE") {
      // ONE_BY_ONE_TRUE Mode: must pick correct answer
      if (!opt?.isCorrect) {
        toast.error("Your selected answer is wrong. Please select the correct answer.");
        setWrongClickOptId(`${q.id}-${optIndex}`);
        setTimeout(() => setWrongClickOptId(null), 1200);
        return;
      }

      // Selected answer is correct!
      toast.success("Correct answer!");
      setWrongClickOptId(null);
      setUserAnswers((prev) => ({ ...prev, [q.id]: optIndex }));
      setSolvedMap((prev) => ({ ...prev, [q.id]: true }));
      return;
    }

    // Default, ALL, or ONE_BY_ONE_TRUE_FALSE mode: select any option without immediate correctness check
    setUserAnswers((prev) => ({
      ...prev,
      [q.id]: optIndex,
    }));
  };

  // Navigation: Next question in one-by-one modes
  const handleNextQuestion = () => {
    const currentQ = questions[currentQuestionIndex];

    if (displayMode === "ONE_BY_ONE_TRUE") {
      const isSolved = solvedMap[currentQ?.id] || isQuestionAnsweredCorrectly(currentQ);
      if (!isSolved) {
        toast.error("Your selected answer is wrong. Please select the correct answer.");
        return;
      }
    }

    if (displayMode === "ONE_BY_ONE_TRUE_FALSE") {
      if (userAnswers[currentQ?.id] === undefined) {
        toast.error("Please select an answer before proceeding.");
        return;
      }
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  // Submit and evaluate
  const handleSubmitAssessment = () => {
    if (locked) {
      toast.error("You have already submitted this stage.");
      setSubmitted(true);
      return;
    }

    console.log("[StageQuestionsView] Submitting assessment:", {
      totalQuestions: questions.length,
      answers: userAnswers,
      displayMode,
    });

    // Check if user answered at least one in ALL mode
    if (displayMode === "ALL" && Object.keys(userAnswers).length === 0) {
      toast.error("Please answer the questions before submitting.");
      return;
    }

    if (displayMode === "ONE_BY_ONE_TRUE") {
      const currentQ = questions[currentQuestionIndex];
      const isSolved = solvedMap[currentQ?.id] || isQuestionAnsweredCorrectly(currentQ);
      if (!isSolved) {
        toast.error("Your selected answer is wrong. Please select the correct answer.");
        return;
      }
    }

    if (displayMode === "ONE_BY_ONE_TRUE_FALSE") {
      const currentQ = questions[currentQuestionIndex];
      if (userAnswers[currentQ?.id] === undefined) {
        toast.error("Please select an answer before proceeding.");
        return;
      }
    }

    // Save Total / Attempted / Correct / Wrong and each attempt for this student
    // (Not Attempted, attemptedCount, correctCount and score are worked out by the backend)
    const projectId = project?.id || stage?.projectId;
    if (projectId) {
      const attempts = [];
      questions.forEach((q) => {
        const ans = userAnswers[q.id];
        if (ans === undefined || ans === null) return;
        attempts.push({
          questionId: q.id,
          selectedOptionId: q.options?.[ans]?.id,
          isCorrect: isQuestionAnsweredCorrectly(q),
        });
      });
      const attempted = attempts.length;
      const correct = attempts.filter((a) => a.isCorrect).length;
      stemProgressApi
        .saveStageEvaluation(stage.id, projectId, {
          totalQuestions: questions.length,
          attempted,
          correct,
          wrong: attempted - correct,
          answers: userAnswers,
          attempts,
        })
        .catch((err) => {
          if (err.status === 409) {
            // Submitted already (e.g. in another tab): show the saved answer sheet instead
            toast.error("You have already submitted this stage.");
            if (err.data) showSavedResult(err.data);
            return;
          }
          console.error("[StageQuestionsView] Failed to save evaluation:", err);
          toast.error("Could not save your result. Please check your connection.");
        });
    }

    setSubmitted(true);
    setLocked(true);
    toast.success("Assessment submitted successfully!");
    if (onCompleteStage) {
      onCompleteStage(stage.id);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "35px", color: "var(--mute)" }}>
        <Loader2 size={22} style={{ animation: "spin 1s linear infinite", margin: "0 auto 8px" }} />
        <p style={{ fontSize: "12.5px" }}>Loading stage questions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "14px", borderRadius: "8px", background: "var(--red-soft)", color: "var(--red-ink)", fontSize: "12.5px" }}>
        {error}
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "35px", color: "var(--mute)" }}>
        <CircleDashed size={28} style={{ margin: "0 auto 8px" }} />
        <p style={{ fontSize: "13.5px", fontWeight: "600", color: "var(--mute)" }}>
          No questions found for this stage.
        </p>
      </div>
    );
  }

  // Calculate evaluation stats
  const totalQuestions = questions.length;
  let attemptedCount = 0;
  let correctCount = 0;
  let wrongCount = 0;

  questions.forEach((q) => {
    const ans = userAnswers[q.id];
    if (ans !== undefined && ans !== null) {
      attemptedCount += 1;
      if (isQuestionAnsweredCorrectly(q)) {
        correctCount += 1;
      } else {
        wrongCount += 1;
      }
    }
  });

  const notAttemptedCount = totalQuestions - attemptedCount;
  const scorePercentage = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;

  // Progressive progress calculation
  const progressCount =
    displayMode === "ONE_BY_ONE_TRUE"
      ? Object.keys(solvedMap).length
      : Object.keys(userAnswers).length;
  const progressPercentage = Math.round((progressCount / totalQuestions) * 100);

  // ==========================================
  // RENDER: SUBMITTED EVALUATION VIEW
  // ==========================================
  if (submitted) {
    return (
      <div>
        {/* Evaluation Summary Header */}
        <div
          style={{
            background: "var(--bg)",
            borderRadius: "10px",
            border: "1px solid var(--line)",
            padding: "16px 18px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
              marginBottom: "14px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Award size={20} color="var(--green)" />
              <div>
                <h3 style={{ fontSize: "15px", fontWeight: "700", color: "var(--ink)", margin: 0 }}>
                  Assessment Evaluation & Results
                </h3>
                <span style={{ fontSize: "11.5px", color: "var(--mute)" }}>
                  Mode: {displayMode} • Completed Successfully
                </span>
              </div>
            </div>

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: scorePercentage >= 50 ? "var(--green-soft)" : "var(--red-soft)",
                color: scorePercentage >= 50 ? "var(--green)" : "var(--red)",
                padding: "4px 12px",
                borderRadius: "20px",
                fontWeight: "700",
                fontSize: "12.5px",
              }}
            >
              <span>Score: {scorePercentage}%</span>
            </div>
          </div>

          {/* 5 Summary Statistics Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(105px, 1fr))",
              gap: "8px",
            }}
          >
            <div
              style={{
                background: "var(--white)",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                padding: "8px 10px",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: "10.5px", color: "var(--mute)", fontWeight: "600", display: "block" }}>
                Total Questions
              </span>
              <span style={{ fontSize: "16px", fontWeight: "800", color: "var(--ink)" }}>
                {totalQuestions}
              </span>
            </div>

            <div
              style={{
                background: "var(--white)",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                padding: "8px 10px",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: "10.5px", color: "var(--blue-2)", fontWeight: "600", display: "block" }}>
                Attempted
              </span>
              <span style={{ fontSize: "16px", fontWeight: "800", color: "var(--blue-2)" }}>
                {attemptedCount}
              </span>
            </div>

            <div
              style={{
                background: "var(--white)",
                border: "1px solid var(--green-line)",
                borderRadius: "8px",
                padding: "8px 10px",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: "10.5px", color: "var(--green)", fontWeight: "600", display: "block" }}>
                Correct
              </span>
              <span style={{ fontSize: "16px", fontWeight: "800", color: "var(--green)" }}>
                {correctCount}
              </span>
            </div>

            <div
              style={{
                background: "var(--white)",
                border: "1px solid var(--red-line)",
                borderRadius: "8px",
                padding: "8px 10px",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: "10.5px", color: "var(--red)", fontWeight: "600", display: "block" }}>
                Wrong
              </span>
              <span style={{ fontSize: "16px", fontWeight: "800", color: "var(--red)" }}>
                {wrongCount}
              </span>
            </div>

            <div
              style={{
                background: "var(--white)",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                padding: "8px 10px",
                textAlign: "center",
              }}
            >
              <span style={{ fontSize: "10.5px", color: "var(--mute)", fontWeight: "600", display: "block" }}>
                Not Attempted
              </span>
              <span style={{ fontSize: "16px", fontWeight: "800", color: "var(--mute)" }}>
                {notAttemptedCount}
              </span>
            </div>
          </div>
        </div>

        {/* Questions Evaluation Table */}
        <div
          style={{
            background: "var(--white)",
            borderRadius: "10px",
            border: "1px solid var(--line)",
            overflow: "hidden",
            marginBottom: "16px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              background: "var(--bg)",
              borderBottom: "1px solid var(--line)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h4
              style={{
                fontSize: "13.5px",
                fontWeight: "700",
                color: "var(--ink)",
                margin: 0,
              }}
            >
              Questions Evaluation
            </h4>
            <span style={{ fontSize: "11.5px", color: "var(--mute)" }}>
              {questions.length} questions evaluated
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left",
                fontSize: "12.5px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "var(--bg)",
                    borderBottom: "1px solid var(--line)",
                    color: "var(--mute)",
                    fontSize: "11px",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  <th style={{ padding: "10px 12px", width: "45px", textAlign: "center" }}>#</th>
                  <th style={{ padding: "10px 14px", width: "36%" }}>Question</th>
                  <th style={{ padding: "10px 14px", width: "26%" }}>User Attempted</th>
                  <th style={{ padding: "10px 14px", width: "26%" }}>Correct Answer</th>
                  <th style={{ padding: "10px 12px", width: "110px", textAlign: "center" }}>Evaluation</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((q, idx) => {
                  const userAns = userAnswers[q.id];
                  const isAttempted = userAns !== undefined && userAns !== null;

                  let userOption = null;
                  let userOptIdx = -1;
                  if (isAttempted && q.options) {
                    if (typeof userAns === "number" && q.options[userAns]) {
                      userOption = q.options[userAns];
                      userOptIdx = userAns;
                    } else {
                      const foundIdx = q.options.findIndex(
                        (o) => o.id === userAns || o.id === Number(userAns)
                      );
                      if (foundIdx !== -1) {
                        userOption = q.options[foundIdx];
                        userOptIdx = foundIdx;
                      }
                    }
                  }

                  let correctOptIdx = -1;
                  let correctOption = null;
                  if (q.options) {
                    correctOptIdx = q.options.findIndex((opt) => opt.isCorrect === true);
                    if (correctOptIdx >= 0) {
                      correctOption = q.options[correctOptIdx];
                    }
                  }

                  const isCorrect = isAttempted && isQuestionAnsweredCorrectly(q);
                  const status = !isAttempted
                    ? "In Attempt"
                    : isCorrect
                    ? "True"
                    : "False";

                  const questionText = q.body || q.title || `Question ${idx + 1}`;
                  const userText = userOption
                    ? userOption.optionText || userOption.text || ""
                    : "";
                  const correctText = correctOption
                    ? correctOption.optionText || correctOption.text || ""
                    : "";

                  return (
                    <tr
                      key={q.id || idx}
                      style={{
                        borderBottom: "1px solid var(--bg)",
                        background: idx % 2 === 0 ? "var(--white)" : "var(--bg)",
                      }}
                    >
                      {/* # Number */}
                      <td
                        style={{
                          padding: "11px 12px",
                          textAlign: "center",
                          fontWeight: "700",
                          color: "var(--mute)",
                          fontSize: "12px",
                        }}
                      >
                        Q{idx + 1}
                      </td>

                      {/* Question Text */}
                      <td
                        style={{
                          padding: "11px 14px",
                          color: "var(--ink)",
                          fontWeight: "500",
                          lineHeight: "1.4",
                        }}
                      >
                        {questionText}
                      </td>

                      {/* User Attempted */}
                      <td style={{ padding: "11px 14px" }}>
                        {isAttempted && userOption ? (
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
                            {userOptIdx >= 0 && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "18px",
                                  height: "18px",
                                  borderRadius: "50%",
                                  background: isCorrect ? "var(--green-soft)" : "var(--red-soft)",
                                  color: isCorrect ? "var(--green-ink)" : "var(--red-ink)",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  flexShrink: 0,
                                  marginTop: "2px",
                                }}
                              >
                                {String.fromCharCode(65 + userOptIdx)}
                              </span>
                            )}
                            <span
                              style={{
                                color: isCorrect ? "var(--green-ink)" : "var(--red-ink)",
                                fontWeight: "500",
                                lineHeight: "1.4",
                              }}
                            >
                              {userText}
                            </span>
                          </div>
                        ) : (
                          <span
                            style={{
                              color: "var(--mute)",
                              fontStyle: "italic",
                              fontSize: "12px",
                            }}
                          >
                            Not Attempted
                          </span>
                        )}
                      </td>

                      {/* Correct Answer */}
                      <td style={{ padding: "11px 14px" }}>
                        {correctOption ? (
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
                            {correctOptIdx >= 0 && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "18px",
                                  height: "18px",
                                  borderRadius: "50%",
                                  background: "var(--green-soft)",
                                  color: "var(--green-ink)",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  flexShrink: 0,
                                  marginTop: "2px",
                                }}
                              >
                                {String.fromCharCode(65 + correctOptIdx)}
                              </span>
                            )}
                            <span style={{ color: "var(--green-ink)", fontWeight: "500", lineHeight: "1.4" }}>
                              {correctText}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: "var(--mute)", fontStyle: "italic" }}>-</span>
                        )}
                      </td>

                      {/* True / False / In Attempt */}
                      <td style={{ padding: "11px 12px", textAlign: "center" }}>
                        {status === "True" && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              background: "var(--green-soft)",
                              color: "var(--green-ink)",
                              border: "1px solid var(--green-line)",
                              padding: "3px 10px",
                              borderRadius: "12px",
                              fontSize: "11.5px",
                              fontWeight: "700",
                            }}
                          >
                            <CheckCircle2 size={13} strokeWidth={2.5} />
                            <span>True</span>
                          </span>
                        )}
                        {status === "False" && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              background: "var(--red-soft)",
                              color: "var(--red-ink)",
                              border: "1px solid var(--red-line)",
                              padding: "3px 10px",
                              borderRadius: "12px",
                              fontSize: "11.5px",
                              fontWeight: "700",
                            }}
                          >
                            <XCircle size={13} strokeWidth={2.5} />
                            <span>False</span>
                          </span>
                        )}
                        {status === "In Attempt" && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              background: "var(--amber-soft)",
                              color: "var(--amber)",
                              border: "1px solid var(--amber-line)",
                              padding: "3px 10px",
                              borderRadius: "12px",
                              fontSize: "11.5px",
                              fontWeight: "700",
                            }}
                          >
                            <MinusCircle size={13} strokeWidth={2.5} />
                            <span>In Attempt</span>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Next Stage Navigation Banner & Button */}
        <div
          style={{
            background: "var(--green-soft)",
            border: "1px solid var(--green-line)",
            borderRadius: "10px",
            padding: "14px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <h5 style={{ fontSize: "13.5px", fontWeight: "700", color: "var(--green-ink)", margin: "0 0 2px" }}>
              {isLastStage ? "All Stages Completed!" : "Stage Assessment Completed!"}
            </h5>
            <span style={{ fontSize: "12px", color: "var(--green-ink)" }}>
              {isLastStage
                ? "You have completed the assessment. Click Complete to finish the project."
                : "You have completed this stage. Click Next Stage to proceed."}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {!locked && (
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              style={{
                background: "var(--white)",
                color: "var(--ink)",
                border: "1px solid var(--line)",
                borderRadius: "8px",
                padding: "8px 14px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Review Questions
            </button>
            )}

            <button
              type="button"
              onClick={onNextStage}
              style={{
                background: "linear-gradient(var(--blue), var(--blue-2))",
                color: "var(--white)",
                border: "none",
                borderRadius: "8px",
                padding: "8px 18px",
                fontSize: "12.5px",
                fontWeight: "700",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                boxShadow: "0 2px 4px rgba(37, 99, 235, 0.25)",
              }}
            >
              <span>{isLastStage ? "Complete" : "Next Stage"}</span>
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER: ACTIVE QUESTION DISPLAY
  // ==========================================
  const currentQ = questions[currentQuestionIndex] || questions[0];
  const isOneByOne = displayMode === "ONE_BY_ONE_TRUE" || displayMode === "ONE_BY_ONE_TRUE_FALSE";
  const isCurrentSolved = solvedMap[currentQ?.id] || isQuestionAnsweredCorrectly(currentQ);

  return (
    <div>
      {/* 1. Progressive Progress Bar */}
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "11.5px",
            fontWeight: "600",
            color: "var(--mute)",
            marginBottom: "5px",
          }}
        >
          <span>Progress: {progressCount} of {totalQuestions} answered</span>
          <span style={{ color: "var(--blue-2)" }}>{progressPercentage}% Completed</span>
        </div>

        <div
          style={{
            width: "100%",
            height: "6px",
            background: "var(--bg)",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progressPercentage}%`,
              height: "100%",
              background: "var(--blue-2)",
              borderRadius: "6px",
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      {/* 2. Top Header & Question Navigation Row (For One-By-One Modes) */}
      {isOneByOne && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            marginBottom: "14px",
            paddingBottom: "10px",
            borderBottom: "1px solid var(--bg)",
          }}
        >
          {/* Left Arrow Button */}
          <button
            type="button"
            onClick={handlePrevQuestion}
            disabled={currentQuestionIndex === 0}
            style={{
              background: "var(--bg)",
              border: "1px solid var(--line)",
              borderRadius: "6px",
              padding: "5px 8px",
              cursor: currentQuestionIndex === 0 ? "not-allowed" : "pointer",
              opacity: currentQuestionIndex === 0 ? 0.4 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--mute)",
            }}
          >
            <ChevronLeft size={16} />
          </button>

          {/* Horizontal Row of Question Numbers */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              overflowX: "auto",
              padding: "2px 4px",
              flex: 1,
              justifyContent: "center",
            }}
          >
            {questions.map((q, idx) => {
              const isActive = idx === currentQuestionIndex;
              const isAnswered =
                displayMode === "ONE_BY_ONE_TRUE"
                  ? Boolean(solvedMap[q.id])
                  : userAnswers[q.id] !== undefined;

              // In ONE_BY_ONE_TRUE mode: cannot jump ahead past unsolved questions
              const isLocked =
                displayMode === "ONE_BY_ONE_TRUE" &&
                idx > currentQuestionIndex &&
                !solvedMap[questions[idx - 1]?.id];

              return (
                <button
                  key={q.id || idx}
                  type="button"
                  onClick={() => {
                    if (isLocked) {
                      toast.error("Your selected answer is wrong. Please select the correct answer.");
                      return;
                    }
                    setCurrentQuestionIndex(idx);
                  }}
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "6px",
                    border: isActive
                      ? "2px solid var(--blue-2)"
                      : isAnswered
                      ? "1px solid var(--green-line)"
                      : "1px solid var(--line)",
                    background: isActive
                      ? "var(--blue-soft)"
                      : isAnswered
                      ? "var(--green-soft)"
                      : "var(--white)",
                    color: isActive ? "var(--blue)" : isAnswered ? "var(--green-ink)" : "var(--mute)",
                    fontSize: "11px",
                    fontWeight: isActive || isAnswered ? "700" : "500",
                    cursor: isLocked ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "all 0.15s ease",
                  }}
                >
                  {isAnswered ? <CheckCircle2 size={13} /> : idx + 1}
                </button>
              );
            })}
          </div>

          {/* Right Arrow Button */}
          <button
            type="button"
            onClick={handleNextQuestion}
            disabled={currentQuestionIndex === questions.length - 1}
            style={{
              background: "var(--bg)",
              border: "1px solid var(--line)",
              borderRadius: "6px",
              padding: "5px 8px",
              cursor: currentQuestionIndex === questions.length - 1 ? "not-allowed" : "pointer",
              opacity: currentQuestionIndex === questions.length - 1 ? 0.4 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--mute)",
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* 3. Question Presentation according to Mode */}

      {/* --- MODE: ALL (Display all questions on a single screen) --- */}
      {displayMode === "ALL" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "16px" }}>
          {questions.map((q, idx) => {
            const selectedOpt = userAnswers[q.id];

            return (
              <div
                key={q.id || idx}
                style={{
                  background: "var(--bg)",
                  border: "1px solid var(--line)",
                  borderRadius: "8px",
                  padding: "12px 14px",
                }}
              >
                <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--ink)", marginBottom: "10px" }}>
                  <span style={{ color: "var(--blue-2)", marginRight: "6px", fontWeight: "700" }}>
                    Q{idx + 1}.
                  </span>
                  {q.body || q.title || "Question"}
                </div>

                {/* Options 2 per row */}
                {q.options && q.options.length > 0 && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "8px",
                    }}
                  >
                    {q.options.map((opt, optIdx) => {
                      const isSelected = selectedOpt === optIdx;

                      return (
                        <div
                          key={opt.id || optIdx}
                          onClick={() => handleSelectOption(q, optIdx)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            background: isSelected ? "var(--blue-soft)" : "var(--white)",
                            border: isSelected ? "1.5px solid var(--blue-2)" : "1px solid var(--line)",
                            cursor: "pointer",
                            fontSize: "12px",
                            color: "var(--ink)",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <span
                            style={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              border: isSelected ? "2px solid var(--blue-2)" : "1px solid var(--line)",
                              background: isSelected ? "var(--blue-2)" : "var(--bg)",
                              color: isSelected ? "var(--white)" : "var(--mute)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              fontWeight: "700",
                              flexShrink: 0,
                            }}
                          >
                            {String.fromCharCode(65 + optIdx)}
                          </span>
                          <span style={{ flex: 1, wordBreak: "break-word" }}>
                            {opt.optionText || opt.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Submit button for ALL mode */}
          <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "6px" }}>
            <button
              type="button"
              onClick={handleSubmitAssessment}
              style={{
                background: "linear-gradient(var(--blue), var(--blue-2))",
                color: "var(--white)",
                border: "none",
                borderRadius: "6px",
                padding: "8px 20px",
                fontSize: "12.5px",
                fontWeight: "700",
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(37, 99, 235, 0.25)",
              }}
            >
              Submit Assessment
            </button>
          </div>
        </div>
      )}

      {/* --- MODE: ONE_BY_ONE_TRUE --- */}
      {displayMode === "ONE_BY_ONE_TRUE" && currentQ && (
        <div
          style={{
            background: "var(--bg)",
            border: "1px solid var(--line)",
            borderRadius: "8px",
            padding: "16px 18px",
            marginBottom: "14px",
          }}
        >
          <div style={{ fontSize: "13.5px", fontWeight: "600", color: "var(--ink)", marginBottom: "12px" }}>
            <span style={{ color: "var(--blue-2)", marginRight: "6px", fontWeight: "700" }}>
              Q{currentQuestionIndex + 1}.
            </span>
            {currentQ.body || currentQ.title || "Question"}
          </div>

          {/* Hint badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              background: "var(--blue-soft)",
              color: "var(--blue-2)",
              fontSize: "11px",
              padding: "2px 8px",
              borderRadius: "6px",
              marginBottom: "12px",
            }}
          >
            <HelpCircle size={12} />
            <span>Select the correct answer to proceed to the next question.</span>
          </div>

          {/* Options 2 per row */}
          {currentQ.options && currentQ.options.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "10px",
                marginBottom: "14px",
              }}
            >
              {currentQ.options.map((opt, optIdx) => {
                const isSelected = userAnswers[currentQ.id] === optIdx;
                const isWrongClicked = wrongClickOptId === `${currentQ.id}-${optIdx}`;

                let bg = "var(--white)";
                let border = "1px solid var(--line)";
                if (isSelected && opt.isCorrect) {
                  bg = "var(--green-soft)";
                  border = "1.5px solid var(--green)";
                } else if (isWrongClicked) {
                  bg = "var(--red-soft)";
                  border = "1.5px solid var(--red)";
                }

                return (
                  <div
                    key={opt.id || optIdx}
                    onClick={() => handleSelectOption(currentQ, optIdx)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      background: bg,
                      border,
                      cursor: "pointer",
                      fontSize: "12.5px",
                      color: "var(--ink)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        border: isSelected && opt.isCorrect ? "2px solid var(--green)" : "1px solid var(--line)",
                        background: isSelected && opt.isCorrect ? "var(--green)" : "var(--bg)",
                        color: isSelected && opt.isCorrect ? "var(--white)" : "var(--mute)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        fontWeight: "700",
                        flexShrink: 0,
                      }}
                    >
                      {String.fromCharCode(65 + optIdx)}
                    </span>
                    <span style={{ flex: 1, wordBreak: "break-word" }}>
                      {opt.optionText || opt.text}
                    </span>
                    {isSelected && opt.isCorrect && <CheckCircle2 size={16} color="var(--green)" />}
                    {isWrongClicked && <XCircle size={16} color="var(--red)" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* Navigation Controls for ONE_BY_ONE_TRUE */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button
              type="button"
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                background: "var(--white)",
                border: "1px solid var(--line)",
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "12px",
                fontWeight: "600",
                color: currentQuestionIndex === 0 ? "var(--mute)" : "var(--ink)",
                cursor: currentQuestionIndex === 0 ? "not-allowed" : "pointer",
                opacity: currentQuestionIndex === 0 ? 0.5 : 1,
              }}
            >
              <ChevronLeft size={15} />
              <span>Previous</span>
            </button>

            {currentQuestionIndex < questions.length - 1 ? (
              <button
                type="button"
                onClick={handleNextQuestion}
                disabled={!isCurrentSolved}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  background: isCurrentSolved ? "linear-gradient(var(--blue), var(--blue-2))" : "var(--bg)",
                  color: isCurrentSolved ? "var(--white)" : "var(--mute)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 16px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: isCurrentSolved ? "pointer" : "not-allowed",
                  opacity: isCurrentSolved ? 1 : 0.6,
                }}
              >
                <span>Next Question</span>
                <ChevronRight size={15} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmitAssessment}
                disabled={!isCurrentSolved}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  background: isCurrentSolved ? "var(--green)" : "var(--bg)",
                  color: isCurrentSolved ? "var(--white)" : "var(--mute)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 16px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: isCurrentSolved ? "pointer" : "not-allowed",
                  opacity: isCurrentSolved ? 1 : 0.6,
                }}
              >
                <span>Submit Assessment</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* --- MODE: ONE_BY_ONE_TRUE_FALSE --- */}
      {displayMode === "ONE_BY_ONE_TRUE_FALSE" && currentQ && (
        <div
          style={{
            background: "var(--bg)",
            border: "1px solid var(--line)",
            borderRadius: "8px",
            padding: "16px 18px",
            marginBottom: "14px",
          }}
        >
          <div style={{ fontSize: "13.5px", fontWeight: "600", color: "var(--ink)", marginBottom: "12px" }}>
            <span style={{ color: "var(--blue-2)", marginRight: "6px", fontWeight: "700" }}>
              Q{currentQuestionIndex + 1}.
            </span>
            {currentQ.body || currentQ.title || "Question"}
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              background: "var(--blue-soft)",
              color: "var(--blue-2)",
              fontSize: "11px",
              padding: "2px 8px",
              borderRadius: "6px",
              marginBottom: "12px",
            }}
          >
            <HelpCircle size={12} />
            <span>Select an answer, then click Next Question to proceed.</span>
          </div>

          {/* Options 2 per row */}
          {currentQ.options && currentQ.options.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "10px",
                marginBottom: "14px",
              }}
            >
              {currentQ.options.map((opt, optIdx) => {
                const isSelected = userAnswers[currentQ.id] === optIdx;

                return (
                  <div
                    key={opt.id || optIdx}
                    onClick={() => handleSelectOption(currentQ, optIdx)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      background: isSelected ? "var(--blue-soft)" : "var(--white)",
                      border: isSelected ? "1.5px solid var(--blue-2)" : "1px solid var(--line)",
                      cursor: "pointer",
                      fontSize: "12.5px",
                      color: "var(--ink)",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        border: isSelected ? "2px solid var(--blue-2)" : "1px solid var(--line)",
                        background: isSelected ? "var(--blue-2)" : "var(--bg)",
                        color: isSelected ? "var(--white)" : "var(--mute)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        fontWeight: "700",
                        flexShrink: 0,
                      }}
                    >
                      {String.fromCharCode(65 + optIdx)}
                    </span>
                    <span style={{ flex: 1, wordBreak: "break-word" }}>
                      {opt.optionText || opt.text}
                    </span>
                    {isSelected && <CheckCircle2 size={16} color="var(--blue-2)" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* Navigation Controls for ONE_BY_ONE_TRUE_FALSE */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button
              type="button"
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                background: "var(--white)",
                border: "1px solid var(--line)",
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "12px",
                fontWeight: "600",
                color: currentQuestionIndex === 0 ? "var(--mute)" : "var(--ink)",
                cursor: currentQuestionIndex === 0 ? "not-allowed" : "pointer",
                opacity: currentQuestionIndex === 0 ? 0.5 : 1,
              }}
            >
              <ChevronLeft size={15} />
              <span>Previous</span>
            </button>

            {currentQuestionIndex < questions.length - 1 ? (
              <button
                type="button"
                onClick={handleNextQuestion}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  background: userAnswers[currentQ?.id] !== undefined ? "linear-gradient(var(--blue), var(--blue-2))" : "var(--bg)",
                  color: userAnswers[currentQ?.id] !== undefined ? "var(--white)" : "var(--mute)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 16px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <span>Next Question</span>
                <ChevronRight size={15} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmitAssessment}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  background: userAnswers[currentQ?.id] !== undefined ? "var(--green)" : "var(--bg)",
                  color: userAnswers[currentQ?.id] !== undefined ? "var(--white)" : "var(--mute)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 16px",
                  fontSize: "12px",
                  fontWeight: "700",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <span>Submit Assessment</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
